import type {
  AuthenticatedAdmin,
  RoyalSpinCampaignConfig,
  RoyalSpinCampaignDetailResponse,
  RoyalSpinCampaignVersion,
} from "@utopia-well-done-ltd/contracts";
import { royalSpinCampaignConfig } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { asMoment } from "../format.js";

const xts = (amount: string) => ({ amount, currency: "XTS" as const, minorUnitScale: 2 });
export const canonicalDraft: RoyalSpinCampaignConfig = {
  audience: "test",
  bonusCashValues: [
    { amount: xts("100"), conditionalWeightBps: 10000 },
    { amount: xts("500"), conditionalWeightBps: 0 },
    { amount: xts("1000"), conditionalWeightBps: 0 },
  ],
  currency: "XTS",
  depositOffer: {
    activationHours: 48,
    caps: [xts("100000"), xts("20000"), xts("10000")],
    percentageBps: [15000, 5000, 2500],
    wageringMultiplier: 25,
  },
  // Temporary XTS playtest setting; restore 1000 minor units for the locked depositor design.
  eligibilityMinSettledStake: xts("0"),
  freeBet: { expiresAfterHours: 168, stake: xts("200"), winningsWageringMultiplier: 15 },
  jackpot: { amount: xts("5000") },
  // Segment IDs are stable protocol slots. Their labels, rewards and odds are configurable.
  outcomes: [
    {
      centerAngleDegrees: 0,
      label: "Up to 150% Deposit Bonus",
      rewardKind: "DEPOSIT_BONUS",
      segmentId: "deposit_bonus",
      weightBps: 5500,
    },
    {
      centerAngleDegrees: 60,
      label: "XTS 1 Bonus",
      bonusCashAmount: xts("100"),
      rewardKind: "BONUS_CASH",
      segmentId: "bonus_cash",
      weightBps: 2200,
    },
    {
      centerAngleDegrees: 120,
      label: "XTS 2 Free Bet",
      rewardKind: "FREE_BET",
      segmentId: "free_bet",
      weightBps: 1200,
    },
    {
      centerAngleDegrees: 180,
      label: "XTS 5 Bonus",
      bonusCashAmount: xts("500"),
      rewardKind: "BONUS_CASH",
      segmentId: "no_reward",
      weightBps: 600,
    },
    {
      centerAngleDegrees: 240,
      label: "XTS 50 Jackpot Bonus",
      rewardKind: "JACKPOT_BONUS",
      segmentId: "jackpot_bonus",
      weightBps: 200,
    },
    {
      centerAngleDegrees: 300,
      label: "XTS 10 Bonus",
      bonusCashAmount: xts("1000"),
      rewardKind: "BONUS_CASH",
      segmentId: "extra_spin",
      weightBps: 300,
    },
  ],
  rewardExpiryHours: 168,
  streakMultiplierBps: [10000, 15000, 20000],
  streakTiers: [
    { days: 1, multiplierBps: 10000 },
    { days: 3, multiplierBps: 15000 },
    { days: 7, multiplierBps: 20000 },
  ],
  wageringMultiplier: 15,
  wheelId: "casino-royal-spin",
};

function xtsAmount(minorUnits: bigint) {
  return `XTS ${minorUnits / 100n}.${(minorUnits % 100n).toString().padStart(2, "0")}`;
}

export function royalSpinCostPreview(config: RoyalSpinCampaignConfig) {
  const outcome = (kind: string) =>
    BigInt(
      config.outcomes
        .filter((candidate) => candidate.rewardKind === kind)
        .reduce((sum, candidate) => sum + candidate.weightBps, 0),
    );
  const expectedNumerator =
    config.outcomes
      .filter((entry) => entry.rewardKind === "BONUS_CASH")
      .reduce((sum, entry) => {
        const base = entry.bonusCashAmount
          ? BigInt(entry.bonusCashAmount.amount) * 10_000n
          : config.bonusCashValues.reduce(
              (total, value) =>
                total + BigInt(value.amount.amount) * BigInt(value.conditionalWeightBps),
              0n,
            );
        return sum + base * BigInt(entry.weightBps);
      }, 0n) +
    BigInt(config.freeBet.stake.amount) * outcome("FREE_BET") * 10_000n +
    BigInt(config.jackpot.amount.amount) * outcome("JACKPOT_BONUS") * 10_000n;
  const expectedFixedMinor = (expectedNumerator + 50_000_000n) / 100_000_000n;
  const maximumDepositExposure = config.depositOffer.caps.reduce((maximum, cap) => {
    const amount = BigInt(cap.amount);
    return amount > maximum ? amount : maximum;
  }, 0n);
  return { expectedFixedMinor, maximumDepositExposure };
}

export function validateRoyalSpinDraft(draftJson: string, effectiveFrom: string) {
  let input: unknown;
  try {
    input = JSON.parse(draftJson);
  } catch {
    return { error: "Campaign configuration must be valid JSON." } as const;
  }

  const config = royalSpinCampaignConfig.safeParse(input);
  if (!config.success) {
    const issue = config.error.issues[0];
    const field = issue?.path.length ? `${issue.path.join(".")}: ` : "";
    return { error: `${field}${issue?.message ?? "Campaign configuration is invalid."}` } as const;
  }

  const activation = new Date(`${effectiveFrom}Z`);
  if (!effectiveFrom || Number.isNaN(activation.getTime())) {
    return { error: "Choose a valid activation date and time." } as const;
  }
  if (activation.getTime() <= Date.now()) {
    return { error: "Activation must be scheduled in the future." } as const;
  }

  return { config: config.data, effectiveFrom: activation.toISOString() } as const;
}

export function RoyalSpin({ admin }: { admin: AuthenticatedAdmin }) {
  const [campaigns, setCampaigns] = useState<RoyalSpinCampaignVersion[] | null>(null);
  const [killSwitch, setKillSwitch] = useState(false);
  const [selected, setSelected] = useState<RoyalSpinCampaignDetailResponse | null>(null);
  const detailDialog = useRef<HTMLDialogElement>(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftJson, setDraftJson] = useState(JSON.stringify(canonicalDraft, null, 2));
  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    new Date(Date.now() + 3600000).toISOString().slice(0, 16),
  );
  const [lifecycleJustification, setLifecycleJustification] = useState("");
  const [emergencyJustification, setEmergencyJustification] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const mayCreate = can(admin.role, "royal_spin.campaign.initiate");
  const mayApprove = can(admin.role, "royal_spin.campaign.approve");
  const mayDisable = can(admin.role, "royal_spin.emergency.write");
  const draft = useMemo(
    () => validateRoyalSpinDraft(draftJson, effectiveFrom),
    [draftJson, effectiveFrom],
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await api.royalSpinCampaigns(signal);
      setCampaigns(response.campaigns);
      setKillSwitch(response.killSwitchEnabled);
      setError(null);
    } catch (caught) {
      if (!signal?.aborted)
        setError(caught instanceof ApiError ? caught.message : "Could not load Royal Spin.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    if (detailDialog.current?.showModal && !detailDialog.current.open) {
      detailDialog.current.showModal();
    } else {
      detailDialog.current?.setAttribute("open", "");
    }
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [selected]);

  const selectedPreview = useMemo(
    () => (selected ? royalSpinCostPreview(selected.campaign.config) : null),
    [selected],
  );

  async function choose(id: string) {
    try {
      setLifecycleJustification("");
      setSelected(await api.royalSpinCampaign(id));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load campaign history.");
    }
  }

  async function mutate(
    operation: () => Promise<unknown>,
    message: string,
    onSuccess?: () => void,
  ) {
    setPending(true);
    setError(null);
    try {
      await operation();
      setNotice(message);
      onSuccess?.();
      setSelected(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The operation failed.");
    } finally {
      setPending(false);
    }
  }

  function closeDetail() {
    setLifecycleJustification("");
    setSelected(null);
  }

  async function createDraft() {
    if (!("config" in draft)) return;
    await mutate(
      () =>
        api.createRoyalSpinCampaign({
          config: draft.config,
          effectiveFrom: draft.effectiveFrom,
          effectiveUntil: null,
        }),
      "Immutable Royal Spin draft created.",
    );
    setDraftOpen(false);
  }

  return (
    <section className="body">
      <div className="card">
        <div className="card-bar">
          <span className="label-14-strong">Royal Spin campaigns</span>
          <span className={`badge badge-${killSwitch ? "banned" : "active"}`}>
            <span className="dot" />
            {killSwitch ? "Emergency disabled" : "Enabled"}
          </span>
          <span className="spacer" />
          {mayCreate ? (
            <button
              className="btn btn-sm"
              onClick={() => setDraftOpen((value) => !value)}
              type="button"
            >
              New immutable draft
            </button>
          ) : null}
        </div>
        {error ? (
          <p className="banner" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="banner banner-quiet" role="status">
            {notice}
          </p>
        ) : null}
        {draftOpen ? (
          <div className="royal-spin-editor">
            <label className="field">
              Activation schedule (UTC)
              <input
                className="input"
                type="datetime-local"
                value={effectiveFrom}
                onChange={(event) => setEffectiveFrom(event.target.value)}
              />
            </label>
            <label className="field">
              Campaign configuration
              <textarea
                className="input textarea mono-12 royal-spin-json"
                value={draftJson}
                onChange={(event) => setDraftJson(event.target.value)}
              />
            </label>
            <p className="label-13 dim">
              Only XTS/test campaigns are accepted. Outcome and conditional weights must each total
              exactly 10,000 bps.
            </p>
            {"error" in draft ? (
              <p className="banner" role="alert">
                {draft.error}
              </p>
            ) : null}
            <button
              className="btn"
              disabled={pending || "error" in draft}
              onClick={() => void createDraft()}
              type="button"
            >
              Create version
            </button>
          </div>
        ) : null}
        <div className="table-scroll">
          <table className="mobile-cards">
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Lifecycle</th>
                <th scope="col">Audience</th>
                <th scope="col">Effective window</th>
                <th scope="col">Creator</th>
              </tr>
            </thead>
            <tbody>
              {campaigns?.map((campaign) => (
                <tr
                  aria-label={`Open campaign ${campaign.id}`}
                  className={`clickable-row${selected?.campaign.id === campaign.id ? " selected" : ""}`}
                  key={campaign.id}
                  onClick={() => void choose(campaign.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void choose(campaign.id);
                    }
                  }}
                  tabIndex={0}
                >
                  <td className="mono-12" data-label="Version">
                    {campaign.id}
                  </td>
                  <td data-label="Lifecycle">
                    <span className="badge">{campaign.lifecycle}</span>
                  </td>
                  <td data-label="Audience">
                    {campaign.config.audience} / {campaign.config.currency}
                  </td>
                  <td className="label-13" data-label="Effective window">
                    {asMoment(campaign.effectiveFrom)} –{" "}
                    {campaign.effectiveUntil ? asMoment(campaign.effectiveUntil) : "open"}
                  </td>
                  <td className="mono-12" data-label="Creator">
                    {campaign.createdBy ?? "system seed"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && selectedPreview ? (
        <dialog
          aria-labelledby="campaign-detail-title"
          className="round-dialog royal-spin-dialog"
          onCancel={closeDetail}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeDetail();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") closeDetail();
          }}
          ref={detailDialog}
        >
          <div className="round-dialog-head">
            <div>
              <h2 className="heading-20" id="campaign-detail-title">
                Campaign detail
              </h2>
              <p className="mono-12 dim">{selected.campaign.id}</p>
            </div>
            <button className="btn btn-quiet btn-sm" onClick={closeDetail} type="button">
              Close
            </button>
          </div>
          <div className="round-dialog-body">
            <div className="royal-spin-detail">
              <div>
                <span className="label-13 dim">Fixed-reward expected cost / spin</span>
                <strong>{xtsAmount(selectedPreview.expectedFixedMinor)}</strong>
              </div>
              <div>
                <span className="label-13 dim">Maximum deposit-offer exposure</span>
                <strong>{xtsAmount(selectedPreview.maximumDepositExposure)}</strong>
              </div>
              <div>
                <span className="label-13 dim">Weight total</span>
                <strong>
                  {selected.campaign.config.outcomes
                    .reduce((sum, item) => sum + item.weightBps, 0)
                    .toLocaleString()}{" "}
                  bps
                </strong>
              </div>
            </div>
            <div className="table-scroll">
              <table className="mobile-cards">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th>Label</th>
                    <th>Angle</th>
                    <th>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.campaign.config.outcomes.map((outcome) => (
                    <tr key={outcome.segmentId}>
                      <td data-label="Segment">{outcome.segmentId}</td>
                      <td data-label="Label">{outcome.label}</td>
                      <td data-label="Angle">{outcome.centerAngleDegrees}°</td>
                      <td data-label="Odds">{(outcome.weightBps / 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="royal-spin-editor">
              <label className="field">
                Audited justification
                <textarea
                  className="input textarea"
                  minLength={20}
                  value={lifecycleJustification}
                  onChange={(event) => setLifecycleJustification(event.target.value)}
                />
              </label>
              <div className="search-row">
                {mayCreate && selected.campaign.lifecycle === "draft" ? (
                  <button
                    className="btn"
                    disabled={pending || lifecycleJustification.trim().length < 20}
                    onClick={() =>
                      void mutate(
                        () =>
                          api.submitRoyalSpinCampaign(selected.campaign.id, lifecycleJustification),
                        "Campaign submitted for independent approval.",
                        () => setLifecycleJustification(""),
                      )
                    }
                    type="button"
                  >
                    Submit
                  </button>
                ) : null}
                {mayApprove &&
                selected.campaign.lifecycle === "submitted" &&
                selected.campaign.createdBy !== admin.id ? (
                  <button
                    className="btn"
                    disabled={pending || lifecycleJustification.trim().length < 20}
                    onClick={() =>
                      void mutate(
                        () =>
                          api.approveRoyalSpinCampaign(
                            selected.campaign.id,
                            lifecycleJustification,
                          ),
                        "Campaign approved for its scheduled activation.",
                        () => setLifecycleJustification(""),
                      )
                    }
                    type="button"
                  >
                    Approve schedule
                  </button>
                ) : null}
                {selected.campaign.createdBy === admin.id &&
                selected.campaign.lifecycle === "submitted" ? (
                  <span className="label-13 dim">
                    Maker-checker: you cannot approve your own submission.
                  </span>
                ) : null}
              </div>
            </div>
            <div className="card-bar">
              <span className="label-14-strong">Lifecycle history</span>
            </div>
            <div className="table-scroll">
              <table className="mobile-cards">
                <thead>
                  <tr>
                    <th>At</th>
                    <th>Transition</th>
                    <th>Actor</th>
                    <th>Justification</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.history.map((event) => (
                    <tr key={`${event.at}-${event.toLifecycle}-${event.actorAdminId ?? "system"}`}>
                      <td data-label="At">{asMoment(event.at)}</td>
                      <td data-label="Transition">
                        {event.fromLifecycle ?? "created"} → {event.toLifecycle}
                      </td>
                      <td className="mono-12" data-label="Actor">
                        {event.actorAdminId ?? "system"}
                      </td>
                      <td data-label="Justification">{event.justification}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </dialog>
      ) : null}

      {mayDisable ? (
        <div className="card">
          <div className="card-bar">
            <span className="label-14-strong">Emergency control</span>
          </div>
          <div className="royal-spin-editor">
            <p className="label-13 dim">
              Requires a recently reauthenticated admin session and creates an append-only audit
              record.
            </p>
            <label className="field">
              Reason
              <textarea
                className="input textarea"
                minLength={20}
                value={emergencyJustification}
                onChange={(event) => setEmergencyJustification(event.target.value)}
              />
            </label>
            <button
              className={killSwitch ? "btn" : "btn btn-danger"}
              disabled={pending || emergencyJustification.trim().length < 20}
              onClick={() =>
                void mutate(
                  () => api.setRoyalSpinKillSwitch(!killSwitch, emergencyJustification),
                  killSwitch ? "Royal Spin re-enabled." : "Royal Spin disabled immediately.",
                  () => setEmergencyJustification(""),
                )
              }
              type="button"
            >
              {killSwitch ? "Re-enable Royal Spin" : "Emergency disable"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
